import {useCallback} from 'react';
import type {ReactNode} from 'react';

import {typedMemo} from '#utils/react.js';

import {
  Anchor as AnchorComponent,
  AnchorVariants,
} from '../components/anchor.js';
import type {AnchorProps as AnchorComponentProps} from '../components/anchor.js';
import {Label} from '../components/label.js';
import {hiddenInput} from '../components/style.css.js';
import type {MultiComboBoxContextType} from '../context.js';
import {useMultiComboBoxContext} from '../context.js';
import {SelectedItems} from './selected_items.js';
import type {SelectedItemsProps} from './selected_items.js';
import {ToggleButton} from './toggle_button.js';

export type RenderProps<T = string> = Pick<
  MultiComboBoxContextType<T>,
  'selectedItems' | 'isOpen' | 'removeSelectedItem'
>;

type AnchorProps<T = string> = Partial<
  Pick<AnchorComponentProps, 'variant' | 'isFullWidth' | 'disabled'>
> & {
  asChild?: boolean;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: SelectedItemsProps['placeholder'];
  id?: string;
  name?: string;
  children?: ReactNode | ((props: RenderProps<T>) => ReactNode);
};

const Anchor = typedMemo(function Anchor<T = string>({
  className,
  style,
  children,
  disabled,
  id,
  name,
  asChild = false,
  placeholder: placeholderProp,
  variant: variantProp,
  isFullWidth,
}: AnchorProps<T>) {
  const {
    anchorRef,
    selectedItems,
    isOpen,
    as,
    anchorPlaceholder,
    getLabelProps,
    getDropdownProps,
    removeSelectedItem,
    toggleMenu,
    openMenu,
    itemToString,
  } = useMultiComboBoxContext<T>();
  const resolvedVariant = variantProp ?? AnchorVariants.transparentBordered;
  const placeholder = placeholderProp ?? anchorPlaceholder ?? 'Select values';
  const resolvedChildren =
    typeof children === 'function'
      ? children({selectedItems, isOpen, removeSelectedItem})
      : children;

  const handleAnchorActive = useCallback(() => {
    if (!disabled) {
      toggleMenu();
    }
  }, [disabled, toggleMenu]);

  const handleHiddenInputInteract = useCallback(() => {
    if (!disabled) {
      openMenu();
    }
  }, [disabled, openMenu]);

  const computedIsFullWidth = isFullWidth ?? as === 'ComboBox';

  return (
    <>
      {(id || name) && (
        <input
          id={id}
          name={name ?? id}
          type="text"
          value={
            selectedItems.length > 0
              ? as === 'ComboBox'
                ? itemToString(selectedItems[selectedItems.length - 1])
                : selectedItems.map(item => itemToString(item)).join(',')
              : ''
          }
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className={hiddenInput}
          disabled={disabled}
          onFocus={handleHiddenInputInteract}
          onClick={handleHiddenInputInteract}
        />
      )}
      <AnchorComponent
        {...getDropdownProps({ref: anchorRef})}
        disabled={disabled}
        className={className}
        onKeyDown={handleAnchorActive}
        onClick={handleAnchorActive}
        variant={resolvedVariant}
        isFullWidth={computedIsFullWidth}
        style={style} // eslint-disable-line react/forbid-component-props
      >
        <Label {...getLabelProps()} label={placeholder} />
        {asChild ? (
          resolvedChildren
        ) : (
          <>
            <SelectedItems placeholder={placeholder} />
            <ToggleButton />
          </>
        )}
      </AnchorComponent>
    </>
  );
});

export type {AnchorProps};
export {Anchor};
